import React from 'react';
import { MapPin } from 'lucide-react';
import { Spot } from '../../utils/mockData';
import { motion } from 'framer-motion';
interface MapViewProps {
  spots: Spot[];
  onMarkerClick: (spot: Spot) => void;
  selectedSpotId?: string;
}
export function MapView({
  spots,
  onMarkerClick,
  selectedSpotId
}: MapViewProps) {
  return <div className="relative w-full h-full bg-blue-50 dark:bg-slate-800 overflow-hidden group" data-id="element-19">
      {/* Abstract Map Background Pattern */}
      <div className="absolute inset-0 opacity-20 dark:opacity-10" data-id="element-20">
        <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg" data-id="element-21">
          <defs data-id="element-22">
            <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse" data-id="element-23">
              <path d="M 40 0 L 0 0 0 40" fill="none" stroke="currentColor" strokeWidth="1" className="text-blue-300" data-id="element-24" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#grid)" data-id="element-25" />
        </svg>
      </div>

      {/* Decorative organic shapes for "land" */}
      <div className="absolute top-0 left-0 w-full h-full pointer-events-none" data-id="element-26">
        <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-emerald-100/50 dark:bg-emerald-900/20 rounded-full blur-3xl" data-id="element-27" />
        <div className="absolute bottom-1/3 right-1/4 w-96 h-96 bg-teal-100/50 dark:bg-teal-900/20 rounded-full blur-3xl" data-id="element-28" />
      </div>

      {/* Markers */}
      {spots.map((spot, index) => {
      // Generate pseudo-random positions based on index for demo
      // In a real app, we'd project lat/lng to pixels
      const top = 20 + index * 15 % 60;
      const left = 20 + index * 25 % 60;
      const isSelected = selectedSpotId === spot.id;
      return <motion.button key={spot.id} className="absolute transform -translate-x-1/2 -translate-y-1/2 focus:outline-none group/marker" style={{
        top: `${top}%`,
        left: `${left}%`
      }} onClick={e => {
        e.stopPropagation();
        onMarkerClick(spot);
      }} initial={{
        scale: 0
      }} animate={{
        scale: 1
      }} transition={{
        delay: index * 0.1,
        type: 'spring'
      }} whileHover={{
        scale: 1.2
      }} data-id="element-29">
            <div className={`relative flex flex-col items-center ${isSelected ? 'z-50' : 'z-10'}`} data-id="element-30">
              <div className={`p-2 rounded-full shadow-lg transition-colors duration-300 ${isSelected ? 'bg-accent text-white scale-110' : 'bg-surface text-primary hover:bg-primary hover:text-white'}`} data-id="element-31">
                <MapPin size={24} fill={isSelected ? 'currentColor' : 'none'} data-id="element-32" />
              </div>
              {isSelected && <motion.div initial={{
            opacity: 0,
            y: 5
          }} animate={{
            opacity: 1,
            y: 0
          }} className="absolute top-full mt-2 px-3 py-1 bg-surface rounded-lg shadow-md whitespace-nowrap z-50 pointer-events-none" data-id="element-33">
                  <span className="text-xs font-bold text-text" data-id="element-34">
                    {spot.name}
                  </span>
                </motion.div>}
            </div>
          </motion.button>;
    })}

      {/* Map Controls */}
      <div className="absolute bottom-24 right-4 md:bottom-8 md:right-8 flex flex-col gap-2" data-id="element-35">
        <button className="p-3 bg-surface rounded-xl shadow-lg text-text hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors" data-id="element-36">
          <span className="sr-only" data-id="element-37">Zoom In</span>
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-38">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" data-id="element-39" />
          </svg>
        </button>
        <button className="p-3 bg-surface rounded-xl shadow-lg text-text hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors" data-id="element-40">
          <span className="sr-only" data-id="element-41">Zoom Out</span>
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-42">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" data-id="element-43" />
          </svg>
        </button>
      </div>
    </div>;
}